import React, { useState, useCallback, memo, useEffect, useRef } from 'react';
import T from 'i18n-react';
import moment from 'moment/moment';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tooltip from '@material-ui/core/Tooltip';
import FormHelperText from '@material-ui/core/FormHelperText';

import type { PlanTermWorkingTimeProps } from '../../../../../core/Plans/context';
import Select from '../../../../../core/Plans/Field/Select';
import {
  convertValue,
  translateMinutes,
  workingTimeMinutes,
  ONE_DAY,
} from '../../hooks/useWorkingTime';
import { MenuItemProps } from './WorkingTime';
import TimeRangeTextGroup from './TimeRangeTextGroup';

const useStyles = makeStyles((theme) => ({
  container: {
    flexWrap: 'wrap',
    padding: theme.spacing(2, 0, 2.5),
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
      padding: theme.spacing(0, 0, 2.5),
    },
  },
  timeRangeGroup: {
    padding: theme.spacing(0.5),
  },
  task: {
    [theme.breakpoints.up('md')]: {
      maxWidth: 300,
    },
  },
  separator: {
    [theme.breakpoints.up('sm')]: {
      display: 'inline-block',
    },
  },
  delimiter: {
    margin: theme.spacing(0, 0.5),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

export type WorkingHourProps = PlanTermWorkingTimeProps & {
  onChange?: () => void;
  startTimeValidity?: boolean;
  endTimeValidity?: boolean;
  totalTimesValidity?: boolean;
};

const WorkingHour = memo(
  ({
    startTime,
    endTime,
    totalTimes,
    onChange,
    startTimeValidity,
    endTimeValidity,
    totalTimesValidity,
  }: WorkingHourProps) => {
    const classes = useStyles();
    const [changeStartTime, setChanegeStartTime] = useState<string>();
    const [changeEndTime, setChangeEndTime] = useState<string>();
    const [changeTotalTimes, setChangeTotalTimes] = useState<string>();
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleStartTimeChange = useCallback(
      (event) => {
        setChanegeStartTime((prevChangeStartTime) => {
          const value = convertValue(event.target.value, prevChangeStartTime);
          onChange({
            startTime: value,
          });
          return value;
        });
      },
      [onChange],
    );

    const handleEndTimeChange = useCallback(
      (event) => {
        setChangeEndTime((prevChangeEndTime) => {
          const value = convertValue(event.target.value, prevChangeEndTime);
          onChange({
            endTime: value,
          });
          return value;
        });
      },
      [onChange],
    );

    const handleTotalTimesChange = useCallback(
      (event) => {
        setChangeTotalTimes(event.target.value);
        onChange({
          totalTimes: event.target.value,
        });
      },
      [onChange],
    );

    useEffect(() => {
      const checkStartTime = Number(
        (changeStartTime || startTime).replace(':', ''),
      );
      const checkEndTime = Number((changeEndTime || endTime).replace(':', ''));
      const checkTotalTimes = Number(
        (changeTotalTimes || totalTimes).replace(':', ''),
      );
      const isInputStartTime =
        (changeStartTime || startTime).replace(':', '').length === 4;
      const isInputEndTime =
        (changeEndTime || endTime).replace(':', '').length === 4;
      const isInputWorkingHour = isInputStartTime && isInputEndTime;

      let startTimeValidity =
        !!(changeStartTime || startTime).match(
          /^[0-2][0-9]:[0-5]{2}$|^[0-9]:[0-5][0-9]$/,
        ) && checkStartTime < ONE_DAY;
      let endTimeValidity =
        !!(changeEndTime || endTime).match(
          /^[0-2][0-9]:[0-5]{2}$|^[0-9]:[0-5][0-9]$/,
        ) && checkEndTime < ONE_DAY;
      let totalTimesValidity = checkTotalTimes > 0 && checkTotalTimes < 801;

      if (checkTotalTimes > checkEndTime - checkStartTime) {
        startTimeValidity = false;
        endTimeValidity = false;
        totalTimesValidity = false;
      }
      if (checkStartTime > checkEndTime && isInputWorkingHour) {
        startTimeValidity = false;
        endTimeValidity = false;
        setErrorMessage(
          T.translate('planPage.planTerms.error.invalidWorkingHour') as string,
        );
      } else {
        setErrorMessage('');
      }
      onChange({
        startTimeValidity: startTimeValidity,
        endTimeValidity: endTimeValidity,
        totalTimesValidity: totalTimesValidity,
      });
    }, [changeStartTime, changeEndTime, changeTotalTimes]);

    return (
      <Grid container spacing={1} direction="column">
        <Grid className={classes.container}>
          <Grid
            container
            alignItems="center"
            className={classes.timeRangeGroup}
          >
            <TimeRangeTextGroup
              startTimeText={changeStartTime || startTime}
              endTimeText={changeEndTime || endTime}
              startTimePlaceholder={
                T.translate('planPage.planTerms.startTime') as string
              }
              endTimePlaceholder={
                T.translate('planPage.planTerms.endTime') as string
              }
              toolTipText={T.translate('textFieldTooltip.time') as string}
              startTimeError={startTimeValidity === false}
              endTimeError={endTimeValidity === false}
              onChangeStartTimeText={handleStartTimeChange}
              onChangeEndTimeText={handleEndTimeChange}
            />
            <Grid item className={classes.separator}>
              <Typography>
                {T.translate('planPage.planTerms.hoursRelation')}
              </Typography>
            </Grid>
            <Grid item xs={12} md="auto" className={classes.task}>
              <Select
                selectProps={{
                  value: totalTimes,
                  displayEmpty: true,
                  onChange: handleTotalTimesChange,
                  error: totalTimesValidity === false,
                }}
                menuItemsProps={[
                  {
                    value: '',
                    disabled: true,
                    children: (
                      <Tooltip
                        title={
                          T.translate(
                            'selectFieldTooltip.totalWorkingTime',
                          ) as string
                        }
                      >
                        <Typography
                          component="span"
                          color="textSecondary"
                          variant="inherit"
                        >
                          {T.translate('planPage.workingTimes.totalTime')}
                        </Typography>
                      </Tooltip>
                    ),
                  },
                ].concat(
                  Object.values(workingTimeMinutes).map((workingMinutes) => ({
                    value: moment('2000/01/01 00:00:00')
                      .add(workingMinutes, 'm')
                      .format('HH:mm'),
                    children: translateMinutes(workingMinutes),
                  })) as MenuItemProps[],
                )}
              />
            </Grid>
            <Grid item className={classes.separator}>
              <Typography>
                {T.translate('planPage.planTerms.workHour')}
              </Typography>
            </Grid>
          </Grid>
          {errorMessage.length > 0 && (
            <FormHelperText className={classes.error}>
              {errorMessage}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    );
  },
);

export default WorkingHour;
